import React, { ChangeEvent, MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RolesAPI } from '../../../api/roleApi';
import Input from '../../../components/Input';
import Switch, { SwitchType } from '../../../components/Switch';
import { PerformaceReportPasswordInput } from '../../../components/performace-report-password-input';
import { isValidDomain, normalizeDomain } from '../../../utils/email-domain';
import { addFamilyUserRole, updateFamilyUserRole } from '../../../store/family-user-role/actions';
import { familyUserRoleSelector } from '../../../store/family-user-role/selectors';
import { AddFamilyUserRole, FamilyUserRole, UpdateFamilyUserRole } from '../../../store/family-user-role/types';

type RoleForm = Pick<FamilyUserRole, 'name' | 'value'> & { selfSelectable: boolean; allowedEmailDomains: string[] };

const EMPTY_ROLE: RoleForm = { name: '', value: '', selfSelectable: true, allowedEmailDomains: [] };

type FamilyUserRoleUpsertProps = {
  cancelBtnClick?: (e: MouseEvent<HTMLButtonElement>) => any;
  callback?: (...args: any) => any;
  roleIdToUpdate?: string;
};

const FamilyUserRoleUpsert: React.FC<FamilyUserRoleUpsertProps> = ({
  cancelBtnClick = () => {},
  callback = () => {},
  roleIdToUpdate = '',
}) => {
  const [roleToUpsert, setRoleToUpsert] = React.useState<RoleForm>(EMPTY_ROLE);
  const [message, setMessage] = React.useState('');
  const [hasError, setHasError] = React.useState(false);

  const existingRole = useSelector(familyUserRoleSelector(roleIdToUpdate));

  React.useEffect(() => {
    if (existingRole) {
      setRoleToUpsert({
        name: existingRole.name,
        value: existingRole.value,
        selfSelectable: existingRole.selfSelectable !== false,
        allowedEmailDomains: existingRole.allowedEmailDomains ?? [],
      });
    } else if (!roleIdToUpdate) {
      // Reset form when creating new role
      setRoleToUpsert(EMPTY_ROLE);
      setMessage('');
      setHasError(false);
    }
  }, [existingRole, roleIdToUpdate]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fieldName = e.target.name as 'name' | 'value';
    const fieldValue = e.target.value;

    setRoleToUpsert((prevRole) => {
      const updated = {
        ...prevRole,
        [fieldName]: fieldValue,
      };
      if (fieldName === 'name') {
        updated.value = fieldValue;
      }
      return updated;
    });
  };

  const onDomainsChange = (options: ReadonlyArray<{ value: string }>) => {
    const entered = options.map((o) => normalizeDomain(o.value));
    const invalid = entered.filter((d) => !isValidDomain(d));
    if (invalid.length) {
      setHasError(true);
      setMessage(`Not a valid domain: ${invalid.join(', ')}`);
    }
    setRoleToUpsert((prevRole) => ({
      ...prevRole,
      allowedEmailDomains: Array.from(new Set(entered.filter(isValidDomain))),
    }));
  };

  const dispatch = useDispatch();

  const saveRole = () => {
    if (!!roleIdToUpdate) {
      RolesAPI.updateFamilyUserRole(roleIdToUpdate, {
        ...existingRole!,
        ...roleToUpsert,
      })
        .then((updatedRole) => {
          setHasError(false);
          dispatch<UpdateFamilyUserRole>(updateFamilyUserRole(roleIdToUpdate, updatedRole));
          setMessage('User role updated successfully');
          callback && callback();
        })
        .catch((e) => {
          setHasError(true);
          setMessage(e.message || 'Fail to update user role');
        });
    } else {
      RolesAPI.addFamilyUserRole(roleToUpsert)
        .then((newRole) => {
          setHasError(false);
          dispatch<AddFamilyUserRole>(addFamilyUserRole(newRole));
          setMessage('User role created successfully');
          callback && callback();
        })
        .catch((e) => {
          setMessage(e.message || 'Fail to add user role');
          setHasError(true);
        });
    }
  };

  return (
    <>
      {message ? (
        <div className={`alert alert-${hasError ? 'danger' : 'success'} text-center my-3`} role='alert'>
          <p className='text-center mb-0'>{message}</p>
        </div>
      ) : null}
      <div className='d-flex flex-column'>
        <div>
          <Input
            type='text'
            onChange={onChange}
            name='name'
            value={roleToUpsert.name}
            label='Name'
            containerClass='my-2'
          />
          <Input
            type='text'
            onChange={() => {}} // Value is read-only, automatically synced with name
            name='value'
            value={roleToUpsert.value}
            label='Value'
            containerClass='my-2'
          />
          <small className='text-muted'>Value is automatically set to match the name</small>

          <Switch
            name='selfSelectable'
            prefix='family-user-role-self-selectable'
            label='Visible at registration'
            checked={roleToUpsert.selfSelectable}
            onChange={() => setRoleToUpsert((prevRole) => ({ ...prevRole, selfSelectable: !prevRole.selfSelectable }))}
            switchType={SwitchType.SLIDER}
          />
          <small className='text-muted d-block'>If off, this type can only be assigned by an admin.</small>

          <div className='my-3'>
            <label>Allowed email domains (optional)</label>
            <PerformaceReportPasswordInput
              value={roleToUpsert.allowedEmailDomains.map((d) => ({ label: d, value: d }))}
              onChangeValue={onDomainsChange}
              placeholder='e.g. estating.com, press enter to add...'
            />
            <small className='text-muted'>Leave empty to allow any email. Subdomains are included.</small>
          </div>
        </div>

        <div className='btn-group mt-3 align-self-end'>
          <button className='btn btn-success' onClick={saveRole}>
            {!!roleIdToUpdate ? 'Update' : 'Create'}
          </button>
          <button className='btn btn-light' onClick={cancelBtnClick}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

export default FamilyUserRoleUpsert;
